import axios from 'axios'
import _ from 'lodash'
import migrateUserPrefs from "../migration";

// Lazy load router to avoid circular dependency
let NCSRouter = null;
let routerLoadAttempted = false;

function getRouter() {
    if (!routerLoadAttempted) {
        routerLoadAttempted = true;
        import('../components/app/Routes').then(module => {
            NCSRouter = module.NCSRouter;
        }).catch(err => {
            console.debug('Router not yet available:', err);
        });
    }
    return NCSRouter;
}

/*
 * User Prefs Service for saving, retrieving, and deleting user preferences.
 * ©Copyright 2018, Sensus USA, Inc. All Rights Reserved.
 *
 * Configuration Object:
 *   axiosConfig - url 'DELETE, POST, GET'
 *
 * API:
 *   constructor()                          - Initializes the prefs cache from the server, usually called from the top level route, mixin, or plugin
 *   get (key, default)                     - get the preferences that match the key, if key is not found, return default
 *   put (key, value)                       - save the value into prefs with the given key
 *   delete (key)                           - remove the prefs that match the key
 *   clear()                                - clears all the users preferences (deletes all)
 *   getSessionCache(key, defaultValue)     - get the preferences from local session that match the key, if key is not found, return default.
 *   putSessionCache(key, value)            - save the value into local session prefs with the given key
 *   deleteSessionCache(key)                - remove the prefs that match the key from local session
 */

//make a constructor for when its instantiated, fetches from the server, then caches it. This is a singleton, so once created it doesnt actually get created again!
let userPreferenceCache = undefined
let systemPreferenceCache = undefined
let isUserPrefsCached = false
let isSystemPrefsCached = false
let sessionCache = undefined
// Map to track which keys are associated with which routes
let keyRouteMap = {}

export default new class PrefsService {
    LAST_ROUTE_PARAM = "lastRoute"

    /**
     * Singleton constructor for preferenceService. The preferences are initially fetched from the server and then cached.
     */
    constructor()
    {
        userPreferenceCache = userPreferenceCache || {}
        systemPreferenceCache = systemPreferenceCache || {}
        sessionCache = sessionCache || {}
        keyRouteMap = keyRouteMap || {}
    }

    /**
     * Get the current route name from the router
     * @returns {string|null} The current route name or null if not available
     */
    getCurrentRouteName() {
        try {
            const router = getRouter();
            if (router?.currentRoute?.value) {
                return router.currentRoute.value.name;
            }
        } catch (error) {
            console.debug('Could not get current route name:', error);
        }
        return null;
    }

    /**
     * Get all preference keys associated with a specific route
     * @param route - The route name to get keys for
     * @returns {Array} Array of preference keys for the route
     */
    getKeysRoute(route) {
        if (!route || !keyRouteMap[route]) {
            return [];
        }
        return Array.from(keyRouteMap[route]);
    }

    /**
     * Associate a preference key with the current route name.
     * This is called automatically by get, put, getSessionCache, and putSessionCache.
     * @param key - The preference key to associate with the current route
     */
    associateKeyWithRoutes(key) {
        if (!key) {
            return;
        }
        
        try {
            const router = getRouter();
            if (!router?.currentRoute) {
                return;
            }
            
            const currentRoute = router.currentRoute.value;
            if (currentRoute?.name) {
                const routeName = currentRoute.name;
                
                // Initialize the route entry if it doesn't exist
                if (!keyRouteMap[routeName]) {
                    keyRouteMap[routeName] = new Set();
                }
                
                // Add the key to this route's set
                keyRouteMap[routeName].add(key);
            }
        } catch (error) {
            // Silently handle errors to avoid breaking existing functionality
            console.debug('Could not associate key with route:', error);
        }
    }

    /**
     * Load the preferences stored on the server for the user
     * @returns {Promise<unknown>}
     */
    loadPrefsFromServer()
    {
        let context = this;
        let userPrefs = new Promise((resolve, reject) => {
                axios({url: '/api/userprefs/v1', method: 'GET'}).then((response) => {
                    if (response && response.data && response.data.records && response.data.records.length && response.data.records[0] && response.data.records[0].map)
                    {
                        userPreferenceCache = response.data.records[0].map
                        isUserPrefsCached = true
                        resolve()
                    }
                    else {
                        userPreferenceCache = {}
                        if (response.status !== 200)
                        {
                            reject("User Preference Service response error")
                        }
                    }
                }).catch((error) => {
                    userPreferenceCache = {}
                    console.error("Initialize user preferences failed with error: ", error)
                    reject(error)
                })
            })

            return new Promise((resolve, reject)=>
            {
                Promise.all([userPrefs]).then(function ()
                {
                    resolve(context)
                }).catch((error) =>
                {
                    console.error("Initialize preferences failed with error: ", error)
                    let resolveObject = {context: context, error: error}
                    reject(resolveObject)
                })
            })
        return new Promise((resolve) => {resolve(context)})
    }
    putImportPrefs(bulkUpload){
        let map = {};
        for (const [key, value] of Object.entries(bulkUpload)) {
            let val = value.value;
            let copyValue = _.cloneDeep(val);
            if (userPreferenceCache) {
                let cachedValue = userPreferenceCache[key] && userPreferenceCache[key].value ? userPreferenceCache[key].value : undefined;
                let cachedObject = userPreferenceCache[key] || {};
                if (!_.isEqual(copyValue, cachedValue)) {
                    userPreferenceCache[key] = {
                        ...cachedObject,
                        value: copyValue,
                    };
                }
            }
            map[key] = val;
        }
        migrateUserPrefs();
        return axios({
            url: '/api/userprefs/v1',
            method: "POST",
            data: {
                map: {
                    ...map,
                },
            },
        }).then((response) => {
                return response
            })
            .catch((error) => {
                console.error("Import preferences failed with error: " + error);
                return Promise.reject(error);
            });
        
    }

    /**
     * Save a preference to the server. If the value is the same in the cache, then dont save the preference.
     * Otherwise, send the preference to the server and attempt to save.
     * @param key
     * @param value
     */
    put(key, value) {
        // Automatically associate this key with the current route
        this.associateKeyWithRoutes(key);
        
        let copyValue = _.cloneDeep(value)
        let cachedValue = userPreferenceCache[key] && userPreferenceCache[key].value ? userPreferenceCache[key].value : undefined
        let cachedObject = userPreferenceCache[key] || {}
        let map = {}

        if (_.isEqual(copyValue, cachedValue))
        {
            return
        }

        userPreferenceCache[key] = {
            ...cachedObject,
            value: copyValue
        }

        map[key] = value

        if (isUserPrefsCached) {
            axios({
                url: '/api/userprefs/v1',
                method: 'POST',
                data: {
                    map: {
                        ...map
                    }
                }
            }).then((response) => {
                if (response && response.status == 200) {
                    //console.log("Preferences saved")
                }
                else {
                    throw response
                }
            }).catch((error) => {
                console.error("Put preferences failed with error: " + error)
            })
        }
    }

    /**
     * Get a preference (cached). If the user passes a key, the service will attempt to look up the value in the cache.
     * If none is defined, then the default preference defined in the funtion is returned. NOTE: if the preference is not found
     * and no default value is set, then undefined will be returned.
     *
     * NOTE: if no key is passed, then the cache itself will be returned. Since objects are passed by reference in javascript, it is
     * up to the user's discretion on how they handle this!
     */
    get(key, defaultValue)
    {
        if (key === undefined) {
            return _.cloneDeep(userPreferenceCache)
        }

        // Automatically associate this key with the current route
        this.associateKeyWithRoutes(key);

        let val = userPreferenceCache[key] && userPreferenceCache[key].value ? userPreferenceCache[key].value : undefined

        if (val === undefined) {
            return (
                defaultValue
            )
        }
        return _.cloneDeep(val)

    }

    /**
     * Delete a preference from the cache and server. If no key is passed, then nothing occurs. Otherwise, the value is looked up and deleted
     * in the cache and then on the server
     * @param key
     */
    delete(key) {
        // If removing something that does not exist, avoid sending a message to the server
        if (userPreferenceCache[key] === undefined || userPreferenceCache[key].value === undefined) {
            return
        }

        let value = _.cloneDeep(userPreferenceCache[key].value)
        delete userPreferenceCache[key]

        if (isUserPrefsCached) {
            let localConfigOb = {url: "/api/userprefs/v1"}

            // Append the key as a path parameter to the url
            if (localConfigOb.url[localConfigOb.url.length - 1] !== '/') {
                localConfigOb.url += '/'
            }
            localConfigOb.url += key

            axios({
                ...localConfigOb,
                method: 'DELETE'
            }).then((response) => {
                if (response && response.status == 200) {
                    //console.log("Preferences deleted")
                }
                else {
                    throw response
                }
            }).catch((error) => {
                console.error("Delete preferences failed with error: " + error)
            })
        }

        return value
    }

    /**
     * Save a preference to the server. If the value is the same in the cache, then dont save the preference.
     * Otherwise, send the preference to the server and attempt to save.
     * @param key
     * @param value
    putSystem(key, value) {
        let copyValue = _.cloneDeep(value)
        let cachedValue = systemPreferenceCache[key] && systemPreferenceCache[key].value ? systemPreferenceCache[key].value : undefined
        let cachedObject = systemPreferenceCache[key] || {}
        let map = {}
        if (_.isEqual(copyValue, cachedValue)) {
            return
        }
        systemPreferenceCache[key] = {
            ...cachedObject,
            value: copyValue
        }
        map[key] = value

        if (isSystemPrefsCached) {
            axios({
                ...configObj,
                url: configObj.url + "/system",
                method: 'POST',
                data: {
                    map: {
                        ...map
                    }
                }
            }).then((response) => {
                if (response && response.status == 200) {
                    console.log("Preferences saved")
                }
                else {
                    throw response
                }
            }).catch((error) => {
                console.error("Put preferences failed with error: " + error)
            })
        }
    }
     */

    /**
     * Get a preference (cached). If the user passes a key, the service will attempt to look up the value in the cache.
     * If none is defined, then the default preference defined in the funtion is returned. NOTE: if the preference is not found
     * and no default value is set, then undefined will be returned.
     *
     * NOTE: if no key is passed, then the cache itself will be returned. Since objects are passed by reference in javascript, it is
     * up to the user's discretion on how they handle this!
     * @param key
     * @param defaultValue
     * @returns {*}
    getSystem(key, defaultValue) {
        if (key === undefined) {
            return _.cloneDeep(systemPreferenceCache)
        }

        let val = systemPreferenceCache[key] && systemPreferenceCache[key].value ? systemPreferenceCache[key].value : undefined

        if (val === undefined) {
            return (
                defaultValue
            )
        }
        return _.cloneDeep(val)

    }
     */

    /**
     * Delete a preference from the cache and server. If no key is passed, then nothing occurs. Otherwise, the value is looked up and deleted
     * in the cache and then on the server
     * @param key
    deleteSystem(key) {
        // If removing something that does not exist, avoid sending a message to the server
        if (systemPreferenceCache[key] === undefined || systemPreferenceCache[key].value === undefined) {
            return
        }

        let value = _.cloneDeep(systemPreferenceCache[key].value)
        delete systemPreferenceCache[key]

        if (isSystemPrefsCached) {
            let localConfigOb = {
                ...configObj,
                url: configObj.url + "/system"
            }

            axios({
                ...localConfigOb,
                method: 'DELETE',
                params:{
                    key: key
                }
            }).then((response) => {
                if (response && response.status == 200) {
                    console.log("System Preferences deleted")
                }
                else {
                    throw response
                }
            }).catch((error) => {
                console.error("Delete System preferences failed with error: " + error)
            })
        }

        return value
    }
     */

    /**
     * Get the session preference that matches the key, if not found, return default value
     * @param key key string to lookup, if undefined, return all session preferences
     * @param defaultValue default value to return if key not found
     * @returns {*} session preferences object that matches the key
     */
    getSessionCache(key, defaultValue) {
        if (key === undefined) {
            return _.cloneDeep(sessionCache);
        }
        
        // Automatically associate this key with the current route
        this.associateKeyWithRoutes(key);
        
        return sessionCache[key] === undefined ? defaultValue : _.cloneDeep(sessionCache[key]);
    }

    /**
     * Save the given session preference object to the given key string
     * @param key string to save session preference object to
     * @param value session preference object to save
     */
    putSessionCache(key, value) {
        // Automatically associate this key with the current route
        this.associateKeyWithRoutes(key);
        
        sessionCache[key] = value;
    }

    /**
     * Delete a given preference from the session
     * @param key
     */
    deleteSessionCache(key) {
        if (sessionCache[key] === undefined) {
            return
        }
        delete sessionCache[key]
    }

    /**
     * Delete all preferences from the cache, sessionCache, and the server.
     */
    clear() {
        let userMap = {}
        let systemMap = {}
        for (let prefObj in userPreferenceCache) {
            userMap[prefObj] = userPreferenceCache[prefObj]
        }

        for (let prefObj in systemPreferenceCache) {
            systemMap[prefObj] = systemPreferenceCache[prefObj]
        }

        sessionCache = {}

        if(Object.keys(userMap).length || Object.keys(systemMap).length){
            if (Object.keys(userMap).length) {
                userPreferenceCache = {}
                if (isUserPrefsCached) {

                    axios({
                        url: '/api/userprefs/v1',
                        method: 'DELETE',
                        // data: {
                        //     map: {
                        //         ...userMap
                        //     }
                        // }
                    }).then((response) => {
                        if (response && response.status == 200) {
                            //console.log("Preferences cleared")
                        }
                        else {
                            throw response
                        }
                    }).catch((error) => {
                        console.error("Clear preferences failed with error: " + error)
                    })
                }
            }
            if (Object.keys(systemMap).length) {
                systemPreferenceCache = {}
                if (isSystemPrefsCached) {

                    axios({
                        url: "/api/userprefs/v1/system",
                        method: 'DELETE',
                        // data: {
                        //     map: {
                        //         ...systemMap
                        //     }
                        // }
                    }).then((response) => {
                        if (response && response.status == 200) {
                            //console.log("Preferences cleared")
                        }
                        else {
                            throw response
                        }
                    }).catch((error) => {
                        console.error("Clear preferences failed with error: " + error)
                    })
                }
            }
        } else {
            return
        }
    }

    /**
     * This should be called from any router link to save the route params to local storage.  This gets around the
     * change in vue-router where params are no longer allowed if not in the query string.  The beforeEach trigger
     * in the router will retrieve the params and add them back to the route.
     * @param params the json object to save, it will be stringified and saved in local storage
     */
    setRouterState(params)
    {
        localStorage.setItem (this.LAST_ROUTE_PARAM, JSON.stringify (params))
    }

    /**
     * Clear all preferences (both server prefs and session cache) associated with a specific route.
     * This includes both user preferences stored on the server and session cache entries.
     * @param routeName - The name of the route whose preferences should be cleared (optional, defaults to current route)
     */
    clearRoutePrefs(routeName) {
        const route = routeName || this.getCurrentRouteName();
        const keys = this.getKeysRoute(route);
        
        keys.forEach(key => {
            this.delete(key);
            this.deleteSessionCache(key);
        });
        
        if (route && keyRouteMap[route]) {
            delete keyRouteMap[route];
        }
    }
}
